import React, { useState, useEffect, useContext } from 'react';
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, TablePagination, Grid, Button } from '@mui/material';
import moment from 'moment';
import Sidebar from '../../components/Sidebar/Sidebar';
import DateRangePicker from '../../components/DateRangePicker/DateRangePicker';
import { UserContext } from '../../contexts/UserContext';
import { searchPersonalPerformanceByDate } from '../../utils/apiUtils';
import './PersonalPerformances.scss';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import TicketModal from '../../components/TicketModal/TicketModal';

const Performances = () => {
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [performances, setPerformances] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [page, setPage] = useState(0);
  const [chartData, setChartData] = useState([]);
  const [ticketModalOpen, setTicketModalOpen] = useState(false);
  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const rowsPerPage = 30;

  const { user } = useContext(UserContext);

  const formatDate = (date) => {
    return date ? moment(date).format('DD-MM-YYYY HH:mm') : '';
  };

  useEffect(() => {
    const fetchPerformancesByDate = async () => {
      if (startDate && endDate) {
        try {
          let performancesData = await searchPersonalPerformanceByDate(user ? user.discordId : '', startDate, endDate);

          // Keep the original reply_date for the LineChart
          let performancesDataForChart = performancesData.map(performance => ({
            ...performance,
            reply_date: moment(performance.reply_date).startOf('day').toDate()
          }));

          // Format the reply_date for the table
          let performancesDataForTable = performancesData.map(performance => ({
            ...performance,
            reply_date: new Date(performance.reply_date)
          }));

          setPerformances(performancesDataForTable);

          let dates = performancesDataForChart.map(performance => performance.reply_date);
          dates = [...new Set(dates.map(date => +date))].map(date => new Date(date));
          dates.sort((a, b) => a - b);

          let agents = Array.from(new Set(performancesDataForChart.map(performance => performance.agent_name)));

          let chartData = dates.map(date => {
            let dateObj = { date: moment(date).format('DD-MM-YYYY') };

            agents.forEach(agent => {
              let performancesOnDate = performancesDataForChart.filter(performance => performance.agent_name === agent && +performance.reply_date === +date);
              dateObj[agent] = performancesOnDate.length;
            });

            return dateObj;
          });

          setChartData(chartData);
        } catch (error) {
          console.error('Error fetching performances:', error);
        }
      }
    };

    fetchPerformancesByDate();
  }, [startDate, endDate, user]);

  const handleStartDateChange = (date) => {
    setStartDate(date);
  };

  const handleEndDateChange = (date) => {
    setEndDate(date);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleOpenTicketModal = async (ticketId) => {
    setSelectedTicketId(ticketId);
    setTicketModalOpen(true);
  };

  const handleCloseTicketModal = () => {
    setTicketModalOpen(false);
  };

  const agents = Array.from(new Set(performances.map(performance => performance.agent_name)));

  const filteredPerformances = selectedAgent !== null ? performances.filter(performance => performance.agent_name === selectedAgent) : performances;

  const performancesCount = agents.filter(agent => agent).map(agent => {
    return { agent: agent, count: performances.filter(performance => performance.agent_name === agent).length };
  });

  return (
    <Box className="StyledContainer" style={{ width: '100%' }}>
      <Sidebar />

      <Box className="StyledContent">
        <Typography variant="h2">Productivity</Typography>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Box className="StyledButtonContainer">
              <Box className="StyledCardHalf">
                <Typography variant="h5">Date Range</Typography>
                <DateRangePicker
                  startDate={startDate}
                  endDate={endDate}
                  onStartDateChange={handleStartDateChange}
                  onEndDateChange={handleEndDateChange}
                />
              </Box>
              {startDate && endDate &&
                <>

                  {performancesCount.map(agentData => (
                    <Box key={agentData.agent} className="StyledCard">
                      <Typography variant="h5">Your score, {agentData.agent}</Typography>
                      <Typography variant="body2" className="count">{agentData.count}</Typography>
                    </Box>
                  ))}
                </>
              }
            </Box>
            <Box className="StyledButtonContainer" style={{ width: '85%' }}>
              <Typography variant="h5">Productivity</Typography>
              <ResponsiveContainer width="100%" height={400}>
                <LineChart
                  data={chartData}
                  margin={{
                    top: 5,
                    right: 30,
                    left: 20,
                    bottom: 5,
                  }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  {agents.map((agent, index) => (
                    <Line
                      key={index}
                      type="monotone"
                      dataKey={agent}
                      stroke={`#${Math.floor(Math.random() * 16777215).toString(16)}`}
                      activeDot={{ r: 8 }}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </Grid>
        </Grid>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Agent Name</TableCell>
                <TableCell>Ticket ID</TableCell>
                <TableCell>Ticket Name</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(rowsPerPage > 0
                ? filteredPerformances.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                : filteredPerformances
              ).map((performance) => (
                <TableRow key={performance.ticket_id}>
                  <TableCell>{performance.agent_name}</TableCell>
                  <TableCell>{performance.ticket_id}</TableCell>
                  <TableCell>{performance.ticket_name}</TableCell>
                  <TableCell>{formatDate(performance.reply_date)}</TableCell>
                  <TableCell>
                    <Button variant="contained" onClick={() => handleOpenTicketModal(performance.ticket_id)}>More Details</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[30]}
            component="div"
            count={filteredPerformances.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
          />
        </TableContainer>
      </Box>
      {ticketModalOpen && (
        <TicketModal
          ticketId={selectedTicketId}
          onClose={handleCloseTicketModal}
        />
      )}
    </Box>
  );
};

export default Performances;
